import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Sparkles } from "lucide-react";
import { Subpage, OrderCta } from "@/components/site/Subpage";
import { Seo, SITE_URL } from "@/components/site/Seo";
import { Label } from "@/components/site/Reveal";
import { checkBullet } from "@/lib/bulletChecker";

const SAMPLES = ["Responsible for handling customer complaints and helping the team.", "Cut onboarding time from 3 weeks to 8 days by rebuilding the training checklist."];

const ScoreRing = ({ score }) => {
  const color = score >= 85 ? "var(--teal)" : score >= 60 ? "var(--amber)" : "#f87171";
  const c = 2 * Math.PI * 52;
  return (
    <div className="relative h-36 w-36 shrink-0" data-testid="checker-score">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <motion.circle cx="60" cy="60" r="52" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - score / 100) }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-serif-d text-5xl text-white">{score}</span>
        <span className="font-mono-d text-[11px] text-slate-400">/ 100</span>
      </div>
    </div>
  );
};

const EmailMe = ({ text, result }) => {
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const send = async (e) => {
    e.preventDefault();
    setState("sending");
    setMsg("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/checker/email`, {
        email, bullet: text, score: result.score, failed: result.checks.filter((c) => !c.ok).map((c) => c.id), marketing_opt_in: optIn,
      });
      setState("sent");
    } catch (err) {
      setMsg(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Couldn't send. Please try again.");
      setState("idle");
    }
  };
  if (state === "sent") return <p className="mt-6 rounded-2xl border border-[var(--teal)]/40 p-4 text-[var(--teal)]" data-testid="checker-email-sent">Sent! Check your inbox for your score and tips.</p>;
  return (
    <form onSubmit={send} className="mt-6 rounded-2xl border border-white/10 bg-[var(--navy)] p-5" data-testid="checker-email-form">
      <p className="font-semibold text-white">Email me my score and tips</p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="checker-email-input"
          className="flex-1 rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-white placeholder:text-slate-500 focus:border-[var(--teal)] focus:outline-none" />
        <button disabled={state === "sending"} data-testid="checker-email-button" className="rounded-full bg-white px-6 py-3 font-semibold text-[var(--ink)] disabled:opacity-60">
          {state === "sending" ? "Sending…" : "Send it"}
        </button>
      </div>
      <label className="mt-3 flex items-start gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} className="mt-1 accent-[#2de2d0]" data-testid="checker-optin-checkbox" />
        Also send me occasional resume tips and offers. Unsubscribe anytime.
      </label>
      {msg && <p className="mt-2 text-sm text-[var(--amber)]" role="alert">{msg}</p>}
    </form>
  );
};

export default function BulletChecker() {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const run = (value) => { const v = value ?? text; if (v.trim()) setResult(checkBullet(v)); };
  const jsonLd = { "@context": "https://schema.org", "@type": "WebApplication", name: "Free Resume Bullet Checker", url: `${SITE_URL}/bullet-checker`,
    applicationCategory: "BusinessApplication", operatingSystem: "Any", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    provider: { "@type": "ProfessionalService", name: "Verbulat", url: `${SITE_URL}/` } };

  return (
    <Subpage>
      <Seo title="Free Resume Bullet Checker: Instant Score & Tips | Verbulat" path="/bullet-checker" jsonLd={jsonLd}
        description="Paste one resume bullet and get an instant score with tips: action verb, real numbers, measurable result, length and clarity. Free, no sign-up." />
      <div data-testid="bullet-checker-page">
        <Label>Free tool</Label>
        <h1 className="font-serif-d mt-5 text-5xl leading-[0.95] text-white sm:text-7xl">Score your <span className="italic text-[var(--teal)]">resume bullet.</span></h1>
        <p className="mt-6 text-lg text-slate-300">Paste one bullet. Get an instant score and specific tips. Free, no sign-up, and nothing you type is saved.</p>
        <div className="mt-10 rounded-3xl border border-white/10 bg-[var(--navy)] p-5 sm:p-7">
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={400} data-testid="checker-input"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run(); }}
            placeholder="e.g. Responsible for managing social media accounts."
            className="w-full resize-none rounded-xl border border-white/15 bg-[var(--ink)] px-4 py-3 text-lg text-white placeholder:text-slate-500 focus:border-[var(--teal)] focus:outline-none" />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={() => run()} disabled={!text.trim()} data-testid="checker-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--teal)] px-6 py-3 font-semibold text-[var(--ink)] transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-40">
              <Sparkles className="h-4 w-4" /> Check my bullet
            </button>
            <span className="text-sm text-slate-400">or try:</span>
            {SAMPLES.map((s, i) => (
              <button key={s} onClick={() => { setText(s); run(s); }} data-testid={`checker-sample-${i}`}
                className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 hover:border-[var(--teal)] hover:text-[var(--teal)]">
                {i === 0 ? "a weak one" : "a strong one"}
              </button>
            ))}
          </div>
        </div>
        {result && (
          <motion.section key={result.score + text} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="checker-result">
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <ScoreRing score={result.score} />
              <div>
                <p className="font-serif-d text-4xl text-white" data-testid="checker-grade">{result.grade}</p>
                <p className="mt-2 text-slate-300">Strong bullets = action verb + task + measurable result, with real numbers.</p>
              </div>
            </div>
            <ul className="mt-8 space-y-3">
              {result.checks.map((c) => (
                <li key={c.id} className={`flex gap-4 rounded-2xl border p-4 ${c.ok ? "border-[var(--teal)]/30 bg-[var(--navy)]" : "border-[var(--amber)]/40 bg-[var(--amber)]/5"}`} data-testid={`checker-check-${c.id}`}>
                  {c.ok ? <Check className="mt-0.5 h-5 w-5 shrink-0 text-[var(--teal)]" /> : <X className="mt-0.5 h-5 w-5 shrink-0 text-[var(--amber)]" />}
                  <div className="flex-1">
                    <p className="flex justify-between gap-3 font-semibold text-white">{c.title}<span className="font-mono-d text-xs font-normal text-slate-400">{c.points}/{c.max}</span></p>
                    <p className="mt-1 text-sm text-slate-300">{c.tip}</p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-400">An automatic check of structure, not a judgment of your experience. It can't tell whether a number is accurate; only you can.</p>
            <EmailMe text={text} result={result} />
            <OrderCta text={result.score >= 85 ? "Make every bullet this strong." : "Want us to fix the whole resume?"} />
          </motion.section>
        )}
        <p className="mt-12 text-slate-300">Learn the formula: <Link to="/blog/action-task-result-bullet-formula" className="text-[var(--teal)] hover:underline">action + task + result</Link>.</p>
      </div>
    </Subpage>
  );
}
